import { ConstelaConfiguration, ConstelaConfigurationModule } from '../../../types/ConstelaConfiguration'
import ModuleData, {
  isLightingHeadData,
  isSupportingColumnData,
  LightingHeadData,
  SingularModuleData,
  SupportingColumnData,
  VariableModuleData
} from '../../../types/ModuleData'
import { getCurrentModuleVariation } from '../../model/ModuleData'
import { isDefined } from '../../utils/typeGuards'

export type ResolvedModule<T extends ModuleData> = ConstelaConfigurationModule & {
  data: T
  index: number
}

const enumerateModules = <T extends ModuleData>(modules: ResolvedModule<T>[], skip?: boolean): ResolvedModule<T>[] => {
  if (skip) {
    return modules
  }
  const counts: Record<string, number> = {}
  for (const module of modules) {
    if (typeof counts[module.data.title] === 'undefined') {
      counts[module.data.title] = 0
    } else if (counts[module.data.title] === 0) {
      counts[module.data.title] = 1
    }
  }
  const result: ResolvedModule<T>[] = []
  for (const module of modules) {
    if (counts[module.data.title] === 0) {
      result.push(module)
    } else {
      result.push({
        ...module,
        data: {
          ...module.data,
          title: module.data.title + ' ' + counts[module.data.title]
        }
      })
      counts[module.data.title]++
    }
  }
  return result
}

export const getPreviewData = (
  configuration: ConstelaConfiguration,
  availableModules: ModuleData[],
  uniqueModuleNames?: boolean
): {
  lightingHead: ResolvedModule<LightingHeadData> | undefined
  variableModules: ResolvedModule<VariableModuleData>[]
  singularModules: ResolvedModule<SingularModuleData>[]
  supportingColumn: ResolvedModule<SupportingColumnData> | undefined
} => {
  const lightingHead = getCurrentModuleVariation(
    availableModules.filter(isLightingHeadData).find((x) => x.id === configuration.lightingHead.id),
    configuration.properties,
    configuration.lightingHead.properties
  )
  const modules = configuration.modules
    .map(({ id, properties }, index): ResolvedModule<ModuleData> | undefined => {
      const originalData = availableModules.find((x) => x.id === id)
      const data = originalData
        ? getCurrentModuleVariation(originalData, configuration.properties, properties)
        : undefined
      return data
        ? {
            id,
            properties,
            data,
            index
          }
        : undefined
    })
    .filter(isDefined)
  const supportingColumn = getCurrentModuleVariation(
    availableModules.filter(isSupportingColumnData).find((x) => x.id === configuration.supportingColumn.id),
    configuration.properties,
    configuration.supportingColumn.properties
  )

  return {
    lightingHead: lightingHead
      ? {
          ...configuration.lightingHead,
          data: lightingHead,
          index: 0
        }
      : undefined,
    variableModules: enumerateModules(
      modules.filter((x): x is ResolvedModule<VariableModuleData> => x.data?.type === 'VariableModule'),
      !uniqueModuleNames
    ),
    singularModules: modules.filter((x): x is ResolvedModule<SingularModuleData> => x.data?.type === 'SingularModule'),
    supportingColumn: supportingColumn
      ? {
          ...configuration.supportingColumn,
          data: supportingColumn,
          index: 0
        }
      : undefined
  }
}
